import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Plus, Filter, Calendar, Zap, LayoutList, SlidersHorizontal, ArrowUpDown, ChevronDown, Table, LayoutGrid, KanbanIcon, BarChart3, Eye, EyeOff, Settings2, Trophy, Handshake, Search, X, FileDown, Map as MapIcon, Wallet, Landmark, XOctagon, CalendarOff } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { ehComercialInterno, ehHunter, FUNCOES_COM_RODIZIO } from '../../../src/lib/departamentos';
import { supervisionaComercial } from '../../../src/lib/supervisaoComercial';
import { logAction, logStageEvent, logUsageEvent } from '../../../src/lib/logger';
import { Negociacao, NegociacaoStage, Pipeline, PipelineStage, Profile, Notification } from '../../../types';
import { negociacoesNaoAbertas } from '../../../src/lib/leadAssignedNotifications';
import { useTemContaBancaria } from '../../../src/hooks/useTemContaBancaria';
import KanbanColumn from './KanbanColumn';
import MeuTrabalhoHoje from './MeuTrabalhoHoje';
import { useResultadoMes } from './useResultadoMes';
import CriarVisitaModal from '../../../src/components/CriarVisitaModal';
import { WorkCounter, WorkChip, computeWorkCounts, matchCounter, matchChip, precisaAcao } from './kanbanWork';
import { embedToArray } from '../../../src/lib/utils';
import { aplicarPatch, ehDeOutroFunil } from './realtimeNegociacoes';
import { useNegociacoesRealtime } from './useNegociacoesRealtime';
import NegotiationDetailModal from '../components/NegotiationDetails/NegotiationDetailModal';
import CRMFilterModal from './CRMFilterModal';
import ExportExcelModal from './ExportExcelModal';
import CRMTable from './CRMTable';
import KanbanGraphic from './KanbanGraphic';
import PerformanceDashboard from '../PerformanceDashboard';
import ParceirosModal from '../ParceirosModal';
import { useAuth } from '../../../src/contexts/AuthContext';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import ResponsibleFilter from '../../../src/components/ResponsibleFilter';
import LemitCentralCustosModal from '../components/LemitCentralCustosModal';
import { getEnrichedKeys } from '../../../src/services/lemit';
import { funilDeVendas, lerFunilAtivo, gravarFunilAtivo } from '../../../src/lib/pipelines';
import { chaveResponsaveis, chaveFiltrosComplexos, lerPref, gravarPref } from '../../../src/lib/kanbanPrefs';
import TermosReprovadosModal from './TermosReprovadosModal';
import AvisarAusenciaModal from '../../../src/components/AvisarAusenciaModal';
import { nomeEtapaNaTela } from '../../../src/lib/nomeEtapaNaTela';

// Tooltip instantâneo (sem o delay do `title` nativo) para os botões da barra de
// filtros que viraram ícone-só. Fica escondido enquanto `hidden` for true (ex: dropdown
// do próprio botão já está aberto) pra não sobrepor o menu.
// Fica fora do celular: lá não há hover, e o texto invisível (nowrap) do botão mais à
// direita passava da borda da tela e fazia a página rolar de lado.
const BarraTooltip: React.FC<{ label: string; hidden?: boolean }> = ({ label, hidden }) => (
    <span
        className={`hidden md:block pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-100
            ${hidden ? '' : 'group-hover:opacity-100'}
        `}
    >
        {label}
    </span>
);

// Colunas lidas explicitamente em vez de `*`. A única diferença de conteúdo é ficar de
// fora o `documentos_json` — a coluna mais pesada da tabela e que nada na árvore do
// Kanban lê. Ao acrescentar em `negociacoes` uma coluna que o board precise exibir,
// incluir aqui também (o detalhe da negociação busca a linha completa por id à parte).
// ATENÇÃO: esta lista foi conferida coluna a coluna contra o banco de produção. A
// interface `Negociacao` em types.ts NÃO é o schema — ela declara `user_id`,
// `data_nascimento_cliente`, `notas` e `observacao`, que não existem na tabela. Com
// `select=*` isso passava batido (vinham undefined); numa lista explícita, uma coluna
// inexistente faz o PostgREST rejeitar a query inteira com 42703. Ao incluir coluna
// nova aqui, confirme que ela existe no banco, não só no types.ts.
const NEG_COLUNAS = [
    'id', 'titulo', 'cliente_nome', 'numero_uc', 'responsavel_id', 'stage', 'status',
    'consumo_medio_kwh', 'avaliacao_estrelas', 'origem', 'plataforma', 'campanha', 'anuncio',
    'formulario', 'reside_goias', 'valor_conta_luz', 'pipeline_id', 'favorito', 'proxima_acao', 'score_prioridade',
    'tipo_cliente', 'cpf_cnpj', 'whatsapp', 'email', 'contato_financeiro', 'email_financeiro',
    'whatsapp_financeiro',
    'previsao_fechamento', 'tags', 'fidelidade',
    'plano_modalidade', 'plano_codigo', 'publico', 'multiplicador_plano', 'fator_fidelidade',
    'multiplicador_final', 'nome_representante_legal', 'cpf_representante',
    'contrato_social_data_versao', 'data_nascimento_representante',
    'stage_changed_at', 'pasta_sharepoint_url', 'desconto_status', 'troca_titularidade_status',
    'motivo_perda', 'observacoes_perda', 'desconto_adicional',
    'url_simulacao', 'url_termo', 'termo_link_do_cliente',
    'analise_cadastral_started_at', 'data_assinatura_termo', 'analise_cadastral_minutos_realizados',
    'segmento', 'cidade', 'bairro', 'latitude', 'longitude', 'geocode_status', 'ultima_atividade_at',
    'created_at', 'updated_at',
].join(', ');

// Embeds enxutos. Dois cortes grandes em relação ao que o board pedia antes:
//
//   - `avalistas:negociacao_avalistas(*)` saiu por completo: nada na árvore do Kanban
//     lê esse campo. Quem usa é o AvalistaRecoveryPanel, dentro do modal de detalhe,
//     que já busca a negociação inteira por id.
//   - analise_cadastral caiu de 17 colunas para 2. O board só precisa de decisao_final
//     (+ decisao_final_at para desempate) em isPendenciaDocumental; as outras 15
//     (parecer_credito, credito_*, verificacao_telefonica_*, ressalvas, pendências)
//     serviam só ao TermosReprovadosModal, que agora as busca sob demanda ao abrir —
//     para um punhado de linhas, em vez de vir no payload de milhares.
const NEG_SELECT = `${NEG_COLUNAS}, tarefas(id, status, vencimento, tipo, modalidade, hora_visita, data_conclusao), unidades_consumidoras(numero_uc, cidade), analise_cadastral(decisao_final, decisao_final_at)`;

interface FilterState {
    status: string[];
    nome: string;
    qualificacao: number | null;
    consumoMin: string;
    consumoMax: string;
    dataCriacaoInicio: string;
    dataCriacaoFim: string;
    dataInteracao: string;
    dataTarefa: string;
    origem: string[];
    plataforma: string[];
}

const FILTROS_PADRAO: FilterState = {
    status: [],
    nome: '',
    qualificacao: null,
    consumoMin: '',
    consumoMax: '',
    dataCriacaoInicio: '',
    dataCriacaoFim: '',
    dataInteracao: '',
    dataTarefa: '',
    origem: [],
    plataforma: [],
};

/** Padrão por papel: quem enxerga o funil inteiro começa vendo só as próprias.
 *  O gerente começa em "Todos" (#6530): quase não tem negociação própria, e o funil abria vazio. */
const responsaveisPadrao = (profile?: { id?: string; role?: string } | null): string[] =>
    profile?.id && (profile.role === 'adm' || profile.role === 'gerente_parcerias')
        ? [profile.id]
        : [];

/** 'ig'/'fb' vêm abreviados do lead automático do Meta Ads; o cadastro manual
 *  grava por extenso ("Instagram"/"Facebook"). Aqui os dois casam na mesma opção
 *  — espelha a mesma função em CRMFilterModal.tsx, que monta a lista de opções. */
const normalizePlataforma = (raw?: string | null): string => {
    const v = (raw || '').trim().toLowerCase();
    if (v === 'ig' || v === 'instagram') return 'Instagram';
    if (v === 'fb' || v === 'facebook') return 'Facebook';
    if (!v) return 'Não informado';
    return (raw as string).trim();
};

/** Recorte que o servidor consegue aplicar. Espelha o estado de filtros da tela. */
interface FiltrosServidor {
    selectedResponsibles: string[];
    showPerdidas: boolean;
    complexFilters: FilterState;
}

/** Desloca uma data YYYY-MM-DD em N dias, devolvendo YYYY-MM-DD. */
const deslocaDias = (iso: string, dias: number): string => {
    const d = new Date(iso + 'T12:00:00');
    d.setDate(d.getDate() + dias);
    return d.toISOString().slice(0, 10);
};

/**
 * Traduz para a query o que dá para filtrar no servidor. O filtro do cliente
 * (getFilteredNegociacoes) continua sendo a fonte da verdade e roda por cima — isto aqui
 * só existe para não trafegar linha que seria descartada. Por isso, onde a tradução não
 * é exata, o critério é **errar para o lado largo**: melhor trazer linha a mais (o cliente
 * descarta) do que a menos (sumiria do board).
 *
 * Ficam de fora, por dependerem de dados que não estão na linha: a busca global (casa
 * também com nº/cidade da UC), dataTarefa (depende de tarefas) e showParceiros / a regra
 * de gerente_parcerias (dependem do papel do responsável, que está em profiles). `plataforma`
 * também fica de fora: é texto livre ('ig'/'fb'/"Instagram"/"Facebook"/...) normalizado só no
 * cliente (normalizePlataforma) — não dá pra traduzir num `in` sem duplicar essa regra aqui.
 */
const aplicaFiltrosServidor = (q: any, f: FiltrosServidor): any => {
    let out = q;
    const cf = f.complexFilters;

    // Responsáveis selecionados na tela — normalmente o corte mais forte.
    if (f.selectedResponsibles.length > 0) out = out.in('responsavel_id', f.selectedResponsibles);

    // status IS NULL não é excluído pelo filtro do cliente (a comparação simplesmente não
    // casa), então o `or` preserva os nulos em vez de deixá-los fora pelo neq.
    if (!f.showPerdidas) out = out.or('status.is.null,status.neq.perdido');
    // showTermoReprovado de propósito NÃO entra aqui: `termosReprovados` (o aviso e o
    // modal de termos reprovados do vendedor) é calculado sobre a lista crua, não sobre a
    // filtrada. Cortar essas linhas no servidor esvaziaria o aviso ao desligar o toggle.

    if (cf.status?.length > 0) out = out.in('status', cf.status);

    // 'Desconhecido' representa origem NULL no cliente; nesse caso não dá para traduzir
    // com um `in` simples, então deixa passar tudo e o cliente resolve.
    if (cf.origem?.length > 0 && !cf.origem.includes('Desconhecido')) out = out.in('origem', cf.origem);

    if (cf.qualificacao !== null) out = out.eq('avaliacao_estrelas', cf.qualificacao);

    if (cf.consumoMin) out = out.gte('consumo_medio_kwh', Number(cf.consumoMin));
    if (cf.consumoMax) out = out.lte('consumo_medio_kwh', Number(cf.consumoMax));

    // Um dia de folga de cada lado: o cliente compara no fuso local do navegador e o
    // servidor em timestamptz, e uma borda apertada poderia cortar linha válida.
    if (cf.dataCriacaoInicio) out = out.gte('created_at', deslocaDias(cf.dataCriacaoInicio, -1));
    if (cf.dataCriacaoFim) out = out.lte('created_at', deslocaDias(cf.dataCriacaoFim, 1));
    if (cf.dataInteracao) {
        out = out.gte('updated_at', deslocaDias(cf.dataInteracao, -1));
        out = out.lte('updated_at', deslocaDias(cf.dataInteracao, 1));
    }

    if (cf.nome) out = out.ilike('titulo', `%${cf.nome}%`);

    return out;
};

const KanbanBoard: React.FC = () => {
    const { profile, refreshProfile, user } = useAuth();
    // Parceiro/vendedor parceiro: sem Relatórios, Exportar e contadores de gestão — só o funil dos próprios leads
    const isParceiroRole = profile?.role === 'parceiro' || profile?.role === 'vendedor_parceiro';
    // Hunter (vendedor de rua): toolbar simplificada (sem Filtros, só visão Kanban, busca embaixo como no mobile)
    // Quem supervisiona o Comercial precisa dos filtros para acompanhar a equipe (#6521).
    const isVendedorExterno = profile?.role === 'vendedor' && ehHunter(profile) && !supervisionaComercial(profile);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [negociacoes, setNegociacoes] = useState<Negociacao[]>([]);
    const [loading, setLoading] = useState(true);

    // Pipelines State
    const [pipelines, setPipelines] = useState<Pipeline[]>([]);
    const [activePipelineId, setActivePipelineId] = useState<string | null>(null);
    const [stages, setStages] = useState<PipelineStage[]>([]);
    /** Quantas negociações do usuário existem nos OUTROS funis. Só é calculado
     *  com o quadro vazio, para explicar o vazio em vez de deixar o vendedor achar
     *  que a negociação que ele acabou de criar se perdeu. */
    const [negociacoesEmOutrosFunis, setNegociacoesEmOutrosFunis] = useState<{ id: string; name: string; total: number }[]>([]);

    // Filters
    const [showFilterModal, setShowFilterModal] = useState(false);
    // Perdidas e termos reprovados começam OCULTOS a cada abertura do CRM (pedido de
    // 22/10/2026). Não fica salvo: quem liga em "Personalizar visualização" vê só
    // até sair da tela. Perdidas são ~metade da base, e ocultas nem saem do servidor.
    const [showTermoReprovado, setShowTermoReprovado] = useState(false);
    const [showPerdidas, setShowPerdidas] = useState(false);
    const [showParceiros, setShowParceiros] = useState(true);
    const [complexFilters, setComplexFilters] = useState<FilterState>(FILTROS_PADRAO);
    // Pedir o status no filtro vence o "ocultar": o filtro de status fica salvo por
    // usuário, e quem tinha "Perdido" marcado abriria o quadro vazio sem entender por quê.
    const forcadoPeloFiltro = (status: string) => !!complexFilters.status?.includes(status);
    const mostrarPerdidas = showPerdidas || forcadoPeloFiltro('perdido');
    const mostrarReprovados = showTermoReprovado || forcadoPeloFiltro('termo_reprovado');

    const [selectedResponsibles, setSelectedResponsibles] = useState<string[]>(
        () => responsaveisPadrao(profile),
    );

    const [showPipelineDropdown, setShowPipelineDropdown] = useState(false);
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [sortOption, setSortOption] = useState<string>('created_desc'); // Default sort
    const [viewMode, setViewMode] = useState<'kanban' | 'table'>('kanban');
    const [showRelatoriosDropdown, setShowRelatoriosDropdown] = useState(false);
    const [showComercialDropdown, setShowComercialDropdown] = useState(false);
    const [isLeanView, setIsLeanView] = useState(() => {
        return localStorage.getItem('vigorhub_kanban_lean_view') === 'true';
    });
    const [showDashboard, setShowDashboard] = useState(false);
    const [showPerformanceDashboard, setShowPerformanceDashboard] = useState(false);
    const [showParceirosModal, setShowParceirosModal] = useState(false);
    const [showCustomizeDropdown, setShowCustomizeDropdown] = useState(false);

    const [profiles, setProfiles] = useState<Profile[]>([]);

    // Avisos de "lead atribuído" ainda não vistos (mesma fonte do LeadAssignedPopup:
    // notifications type='negotiation_assigned', read=false) — liga o pulso do card.
    const [naoAbertoIds, setNaoAbertoIds] = useState<Set<number>>(new Set());
    useEffect(() => {
        if (!user?.id) return;
        let cancelado = false;

        const carregarNaoAbertos = async () => {
            const { data } = await supabase
                .from('notifications')
                .select('*')
                .eq('user_id', user.id)
                .eq('type', 'negotiation_assigned')
                .eq('read', false);
            if (!cancelado) setNaoAbertoIds(negociacoesNaoAbertas((data || []) as Notification[]));
        };
        carregarNaoAbertos();

        // Realtime: refaz a busca em qualquer INSERT/UPDATE de notification deste
        // usuário — dataset pequeno (só as não lidas), mais simples e seguro do que
        // remendar o Set manualmente por tipo de evento.
        const channel = supabase
            .channel(`kanban-naoaberto-${user.id}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` }, carregarNaoAbertos)
            .subscribe();

        return () => { cancelado = true; supabase.removeChannel(channel); };
    }, [user?.id]);

    // Banner "complete seus dados bancários" — o link de indicação (código, copiar,
    // WhatsApp) mudou pro Analytics; aqui só falta o banner mesmo.
    const temContaBancaria = useTemContaBancaria(isParceiroRole, profile?.id);
    const [bancoBannerDispensado, setBancoBannerDispensado] = useState(false);

    // Mobile specific
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Global search bar
    const [globalSearch, setGlobalSearch] = useState('');
    const [showExportModal, setShowExportModal] = useState(false);
    const [showLemitCentral, setShowLemitCentral] = useState(false);
    const [showTermosReprovadosModal, setShowTermosReprovadosModal] = useState(false);
    const [showAvisarAusencia, setShowAvisarAusencia] = useState(false);

    // "Meu Trabalho Hoje" — contador ativo (toggle) + chip de filtro (seleção única)
    const [workCounter, setWorkCounter] = useState<WorkCounter | null>(null);
    const [workChip, setWorkChip] = useState<WorkChip>('todos');

    // Criar visita (ação rápida do card)
    const [visitaNegociacao, setVisitaNegociacao] = useState<Negociacao | null>(null);

    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedNegociacao, setSelectedNegociacao] = useState<Negociacao | null>(null);

    // Recorte enviado ao servidor. `filtrosKey` dispara o refetch só quando o conteúdo
    // muda de fato (evita recarregar por um array novo com os mesmos ids), e o ref deixa
    // recarregarBoard/o listener de refreshKanban estáveis sem ficarem com valor velho.
    const filtrosServidor = useMemo<FiltrosServidor>(
        () => ({ selectedResponsibles, showPerdidas: mostrarPerdidas, complexFilters }),
        [selectedResponsibles, mostrarPerdidas, complexFilters],
    );
    const filtrosKey = JSON.stringify(filtrosServidor);
    const filtrosRef = useRef(filtrosServidor);
    filtrosRef.current = filtrosServidor;

    /** Sequencial das cargas — resposta de requisição superada é descartada. */
    const requisicaoAtual = useRef(0);

    /** Responsáveis que este papel enxerga. `null` = sem recorte (vê todos).
     *  Extraído de fetchNegociacoes para o contador de "outros funis" usar
     *  exatamente a mesma regra — duplicar isso daria contagem mentirosa. */
    const escopoResponsaveis = useCallback(async (): Promise<string[] | null> => {
        if (!profile?.id) return null;
        if (profile.role === 'vendedor' && supervisionaComercial(profile)) {
            // Supervisão do Comercial (#6521): as próprias negociações e as das Closers e BDRs.
            const { data: equipe } = await supabase.from('profiles').select('id')
                .in('funcao_comercial', [...FUNCOES_COM_RODIZIO]).eq('status', 'active');
            return [...new Set([profile.id, ...(equipe?.map(m => m.id) || [])])];
        }
        if (profile.role === 'vendedor' || profile.role === 'vendedor_parceiro') {
            return [profile.id];
        }
        if (profile.role === 'parceiro') {
            const { data: managed } = await supabase.from('profiles').select('id').eq('parceiro_id', profile.id);
            return [profile.id, ...(managed?.map(m => m.id) || [])];
        }
        if (profile.role === 'gerente_parcerias') {
            const { data: parceiroUsers } = await supabase.from('profiles').select('id').in('role', ['parceiro', 'vendedor_parceiro']);
            return [profile.id, ...(parceiroUsers?.map(m => m.id) || [])];
        }
        return null;
    }, [profile?.id, profile?.role, profile?.permissions]);

    const fetchNegociacoes = useCallback(async (pipelineId: string, filtros: FiltrosServidor) => {
        const requisicao = ++requisicaoAtual.current;
        setLoading(true);
        try {
            const respScope = await escopoResponsaveis();

            // Escopo obrigatório (funil + papel) + os filtros da tela que dá para resolver
            // no servidor. Filtrar aqui é o que muda o jogo: o board deixa de baixar a
            // tabela inteira do funil para depois jogar fora no cliente.
            const aplicaEscopo = (q: any): any => {
                let out = q.or(`pipeline_id.eq.${pipelineId},pipeline_id.is.null`);
                if (respScope) out = out.in('responsavel_id', respScope);
                return aplicaFiltrosServidor(out, filtros);
            };

            // Supabase corta em 1000 linhas por resposta; com a base acima disso, as
            // negociações antigas sumiam do board — paginamos até esgotar. Com os filtros
            // já aplicados no servidor isso quase sempre é uma requisição só.
            // (Não há count 'exact' antes: numa tabela com RLS ele custa quase o mesmo
            // que a página em si, e era um round-trip serial travando a primeira pintura.)
            const PAGE = 1000;
            const all: any[] = [];
            for (let page = 0; ; page++) {
                const from = page * PAGE;
                const { data, error } = await aplicaEscopo(supabase.from('negociacoes').select(NEG_SELECT))
                    .order('created_at', { ascending: false })
                    .order('id', { ascending: false })
                    .range(from, from + PAGE - 1);
                if (error) throw error;
                const batch = data || [];
                all.push(...batch);
                if (batch.length < PAGE) break;
            }
            // Uma requisição mais nova já saiu enquanto esta voltava: descarta a resposta
            // velha em vez de deixar ela sobrescrever o board.
            if (requisicao !== requisicaoAtual.current) return;

            // embed analise_cadastral vem como objeto (1→1) — kanbanWork e afins leem como array
            all.forEach((n: any) => { n.analise_cadastral = embedToArray(n.analise_cadastral); });

            // Board pinta aqui. O enriquecimento da Lemit abaixo custa vários round-trips
            // e antes rodava *antes* do setNegociacoes, segurando a primeira pintura.
            setNegociacoes(all);
            setLoading(false);

            // Selo "enriquecido pela Lemit" em 2 níveis: empresa/pessoa (casa por
            // negociacao_id OU documento) e dono (CPF do representante consultado).
            try {
                const { negIds: enrIds, docs: enrDocs } = await getEnrichedKeys(
                    all.map((n: any) => Number(n.id)),
                    all.flatMap((n: any) => [String(n.cpf_cnpj || ''), String(n.cpf_representante || '')]),
                );
                if (requisicao !== requisicaoAtual.current) return;
                setNegociacoes(prev => prev.map((n: any) => {
                    const doc = String(n.cpf_cnpj || '').replace(/\D/g, '');
                    const cpfRep = String(n.cpf_representante || '').replace(/\D/g, '');
                    const enriquecido = enrIds.has(Number(n.id)) || (!!doc && enrDocs.has(doc));
                    const dono = cpfRep.length === 11 && enrDocs.has(cpfRep);
                    if (n._lemitEnriquecido === enriquecido && n._lemitDono === dono) return n;
                    return { ...n, _lemitEnriquecido: enriquecido, _lemitDono: dono };
                }));
            } catch (e) { console.error('Lemit enriched flags:', e); }
        } catch (error) {
            console.error('Error fetching negociacoes:', error);
        } finally {
            // Só a requisição mais recente controla o loading: uma carga superada chegando
            // atrasada não pode apagar o indicador de quem ainda está em voo.
            if (requisicao === requisicaoAtual.current) setLoading(false);
        }
    }, [profile?.id, profile?.role, escopoResponsaveis]);

    // Profiles e pipelines não dependem do funil ativo — carregam uma vez, na montagem.
    // (O listener de 'refreshKanban' vive num effect só, mais abaixo: registrá-lo aqui
    // também fazia todo refresh disparar a query pesada de negociações em dobro.)
    // fetchPipelines depende do usuário: a preferência de funil é por conta.
    // O `else if (!activePipelineId)` lá dentro impede que uma reexecução
    // sobrescreva uma escolha manual já feita nesta sessão.
    useEffect(() => {
        fetchProfiles();
        fetchPipelines();
    }, [user?.id]);

    // Etapas: só quando o funil muda.
    useEffect(() => {
        if (activePipelineId) {
            setStages([]); // Clear stages to prevent stale data
            fetchStages(activePipelineId);
        }
    }, [activePipelineId]);

    // Negociações: recarrega quando o funil OU o recorte enviado ao servidor muda.
    // O debounce agrupa cliques seguidos no painel de filtros numa carga só; o
    // sequencial dentro de fetchNegociacoes descarta respostas que chegarem fora de ordem.
    useEffect(() => {
        if (!activePipelineId) return;
        const t = setTimeout(() => fetchNegociacoes(activePipelineId, filtrosRef.current), 250);
        return () => clearTimeout(t);
    }, [activePipelineId, filtrosKey, fetchNegociacoes]);

    // Persist active pipeline (por usuário — a chave global vazava entre contas)
    useEffect(() => {
        if (activePipelineId) gravarFunilAtivo(user?.id, activePipelineId);
    }, [activePipelineId, user?.id]);

    // Quadro vazio: conta as negociações do usuário nos demais funis (HEAD, sem
    // trazer linha). Só roda nesse caso, então é 1-2 requisições raras — e o
    // índice idx_negociacoes_pipeline_created cobre o filtro.
    useEffect(() => {
        let cancelado = false;
        if (loading || negociacoes.length > 0 || !activePipelineId || pipelines.length < 2) {
            setNegociacoesEmOutrosFunis([]);
            return;
        }
        (async () => {
            const respScope = await escopoResponsaveis();
            const outros = pipelines.filter(p => p.id !== activePipelineId);
            // Conta só o que apareceria lá: perdidas e reprovadas ocultas não entram,
            // senão o aviso manda o vendedor para um funil que também abre vazio.
            const ocultos = [
                ...(mostrarPerdidas ? [] : ['perdido']),
                ...(mostrarReprovados ? [] : ['termo_reprovado']),
            ];
            const contagens = await Promise.all(outros.map(async (p) => {
                let q = supabase.from('negociacoes').select('id', { count: 'exact', head: true }).eq('pipeline_id', p.id);
                if (respScope) q = q.in('responsavel_id', respScope);
                if (ocultos.length > 0) q = q.or(`status.is.null,status.not.in.(${ocultos.join(',')})`);
                const { count } = await q;
                return { id: p.id, name: p.name, total: count || 0 };
            }));
            if (!cancelado) setNegociacoesEmOutrosFunis(contagens.filter(c => c.total > 0));
        })().catch(err => console.error('Erro ao contar negociações em outros funis:', err));
        return () => { cancelado = true; };
    }, [loading, negociacoes.length, activePipelineId, pipelines, escopoResponsaveis, mostrarPerdidas, mostrarReprovados]);

    useEffect(() => {
        localStorage.setItem('vigorhub_kanban_lean_view', String(isLeanView));
    }, [isLeanView]);

    /** Filtros salvos são POR USUÁRIO (ver src/lib/kanbanPrefs.ts). A hidratação
     *  é um effect, e não o initializer do useState, porque `user` só chega depois
     *  da primeira pintura — ler no initializer devolveria sempre o padrão. Enquanto
     *  não hidratar, os effects de gravação abaixo não escrevem: senão o estado
     *  padrão sobrescreveria o filtro salvo antes de ele ser lido. */
    const hidratadoPara = useRef<string | null>(null);
    useEffect(() => {
        const uid = user?.id;
        if (!uid || hidratadoPara.current === uid) return;
        const resp = lerPref<string[]>(chaveResponsaveis(uid));
        const filtros = lerPref<Partial<FilterState>>(chaveFiltrosComplexos(uid));
        setSelectedResponsibles(Array.isArray(resp) ? resp : responsaveisPadrao(profile));
        // `{ ...padrao, ...salvo }` absorve filtro gravado por uma versão anterior
        // do FilterState sem faltar campo.
        setComplexFilters(filtros ? { ...FILTROS_PADRAO, ...filtros } : FILTROS_PADRAO);
        hidratadoPara.current = uid;
    }, [user?.id, profile?.id, profile?.role]);

    useEffect(() => {
        if (hidratadoPara.current !== user?.id) return;
        gravarPref(user?.id ? chaveResponsaveis(user.id) : null, selectedResponsibles);
    }, [selectedResponsibles, user?.id]);

    useEffect(() => {
        if (hidratadoPara.current !== user?.id) return;
        gravarPref(user?.id ? chaveFiltrosComplexos(user.id) : null, complexFilters);
    }, [complexFilters, user?.id]);

    // Auto-move 'termo_assinado' status negotiations to the 'Termo de Adesão' stage safely
    useEffect(() => {
        if (stages.length > 0 && negociacoes.length > 0) {
            // Find the exact correct string name from the database to avoid breaking the Kanban columns
            const termoStageName = stages.find(s => s.name.toLowerCase().includes('termo de ades'))?.name;
            if (!termoStageName) return;

            const needsUpdate = negociacoes.filter(n => n.status === 'termo_assinado' && n.stage !== termoStageName);

            if (needsUpdate.length > 0) {
                // Background update for database (self-healing for corrupted names too)
                needsUpdate.forEach(n => {
                    const oldStage = n.stage;
                    supabase
                        .from('negociacoes')
                        .update({ stage: termoStageName, updated_at: new Date().toISOString() })
                        .eq('id', n.id)
                        .then(async ({ error }) => {
                            if (!error) {
                                await logAction(user?.id, 'UPDATE_NEGOCIACAO_STAGE_AUTO', {
                                    negociacao_id: n.id,
                                    old_stage: oldStage,
                                    new_stage: termoStageName,
                                    reason: 'statusChangedToTermoAssinado'
                                });
                                logStageEvent({
                                    negociacaoId: n.id,
                                    userId: user?.id,
                                    fromStage: oldStage,
                                    toStage: termoStageName,
                                    fromStatus: n.status,
                                    toStatus: n.status,
                                });
                            }
                        });
                });

                // Immediate local state update
                setNegociacoes(prev => prev.map(n =>
                    (n.status === 'termo_assinado' && n.stage !== termoStageName) ? { ...n, stage: termoStageName } : n
                ));
            }
        }
    }, [negociacoes, stages]);

    // Recarrega o board com o recorte atual. Identidade estável (lê os filtros do ref) para
    // o listener de refreshKanban não se re-registrar a cada mudança de filtro.
    const recarregarBoard = useCallback(() => {
        if (activePipelineId) fetchNegociacoes(activePipelineId, filtrosRef.current);
    }, [activePipelineId, fetchNegociacoes]);

    // Leva o quadro até o funil de uma negociação recém-criada. Sem isso ela nasce
    // fora da vista quando o funil ativo é outro — o quadro filtra por pipeline_id e
    // não dá sinal nenhum de que existem negociações em outro funil.
    const seguirFunilERecarregar = useCallback((pipelineId?: string | null) => {
        if (pipelineId && pipelineId !== activePipelineId && pipelines.some(p => p.id === pipelineId)) {
            // Trocar o funil já dispara a carga (effect de activePipelineId); recarregar
            // aqui também faria a query pesada do board sair duas vezes.
            setActivePipelineId(pipelineId);
            return;
        }
        recarregarBoard();
    }, [activePipelineId, pipelines, recarregarBoard]);

    // Listener para o evento refreshKanban (usado por modals e DetailFeed).
    // `detail.pipelineId` é opcional: os disparos antigos continuam só recarregando.
    useEffect(() => {
        const aoRefresh = (e: Event) =>
            seguirFunilERecarregar((e as CustomEvent<{ pipelineId?: string }>).detail?.pipelineId);
        window.addEventListener('refreshKanban', aoRefresh);
        return () => window.removeEventListener('refreshKanban', aoRefresh);
    }, [seguirFunilERecarregar]);

    // Arraste em andamento: o board não pode ter a lista trocada embaixo do dedo do
    // usuário (o card salta da mão e o @hello-pangea/dnd perde a referência do item).
    const [arrastando, setArrastando] = useState(false);

    /**
     * Recarrega só as linhas que o tempo real apontou, com **exatamente** o mesmo recorte
     * da carga cheia (funil + papel + filtros de servidor). Reaproveitar o recorte é o que
     * garante que o patch nunca traga para a tela uma linha que um F5 não traria — e que
     * uma linha que saiu do recorte suma do board (aplicarPatch remove id pedido que não
     * voltou na resposta).
     */
    const aplicarMudancasRealtime = useCallback(async (ids: number[]) => {
        const pipelineId = activePipelineId;
        if (!pipelineId || ids.length === 0) return;
        // Mesmo sequencial da carga cheia: se uma carga sair enquanto este patch está no ar,
        // ela é mais nova e já inclui estas linhas — aplicar por cima reintroduziria dado velho.
        const requisicao = requisicaoAtual.current;
        // Fotografado antes do primeiro await: define quem é "linha nova" para o selo Lemit
        // lá embaixo, sem depender de quando o effect de idsNaTelaRef roda.
        const idsAntes = new Set(idsNaTelaRef.current);
        const respScope = await escopoResponsaveis();

        let q = supabase.from('negociacoes').select(NEG_SELECT).in('id', ids)
            .or(`pipeline_id.eq.${pipelineId},pipeline_id.is.null`);
        if (respScope) q = q.in('responsavel_id', respScope);
        const { data, error } = await aplicaFiltrosServidor(q, filtrosRef.current);
        if (error) throw error;

        if (requisicao !== requisicaoAtual.current) return;

        const linhas = (data || []).map((n: any) => ({ ...n, analise_cadastral: embedToArray(n.analise_cadastral) }));
        setNegociacoes(prev => aplicarPatch(prev, linhas as Negociacao[], ids));

        // O modal de detalhe guarda uma cópia da negociação. Sem isto, a mudança aparecia
        // no card atrás e o modal aberto por cima continuava com o dado velho. Só atualiza
        // se a linha voltou na resposta — sumindo do recorte, é melhor manter o que está na
        // tela do que fechar/esvaziar o modal debaixo do usuário.
        setSelectedNegociacao(prev => {
            if (!prev) return prev;
            const nova = linhas.find((n: any) => Number(n.id) === Number(prev.id));
            return nova ? ({ ...prev, ...nova } as Negociacao) : prev;
        });

        // Selo Lemit só das linhas que ENTRARAM agora. Para as que já estavam na tela o
        // aplicarPatch preserva o selo anterior — consultar de novo seria um par de
        // requisições extra em cada evento, que é o caso comum (edição de card visível).
        const novas = linhas.filter((n: any) => !idsAntes.has(Number(n.id)));
        if (novas.length === 0) return;
        try {
            const idsNovas = novas.map((n: any) => Number(n.id));
            const { negIds, docs } = await getEnrichedKeys(
                idsNovas,
                novas.flatMap((n: any) => [String(n.cpf_cnpj || ''), String(n.cpf_representante || '')]),
            );
            if (requisicao !== requisicaoAtual.current) return;
            setNegociacoes(prev => prev.map((n: any) => {
                if (!idsNovas.includes(Number(n.id))) return n;
                const doc = String(n.cpf_cnpj || '').replace(/\D/g, '');
                const cpfRep = String(n.cpf_representante || '').replace(/\D/g, '');
                const enriquecido = negIds.has(Number(n.id)) || (!!doc && docs.has(doc));
                const dono = cpfRep.length === 11 && docs.has(cpfRep);
                if (n._lemitEnriquecido === enriquecido && n._lemitDono === dono) return n;
                return { ...n, _lemitEnriquecido: enriquecido, _lemitDono: dono };
            }));
        } catch (e) { console.error('Lemit enriched flags (realtime):', e); }
    }, [activePipelineId, escopoResponsaveis]);

    // Ids que estão na tela agora, para o descarte barato de evento abaixo. Ref, e não
    // estado: só é lido dentro do callback do canal, e derivar por estado faria o canal
    // enxergar valor velho ou se re-registrar a cada carga.
    const idsNaTelaRef = useRef<Set<number>>(new Set());
    useEffect(() => { idsNaTelaRef.current = new Set(negociacoes.map(n => Number(n.id))); }, [negociacoes]);

    /** Evita gastar uma requisição com a mudança de um funil que não é o aberto. Só descarta
     *  quando o evento **não** é de uma linha que está na tela (essa pode precisar sumir) e o
     *  `pipeline_id` novo é outro funil, não-nulo (o board também mostra pipeline_id nulo).
     *  Payload sem `new` (DELETE) ou truncado não é descartado — na dúvida, consulta. */
    const eventoDeOutroFunil = useCallback(
        (payload: { new?: any }) => ehDeOutroFunil(payload, activePipelineId, idsNaTelaRef.current),
        [activePipelineId],
    );

    // Tempo real do board. Antes daqui, o quadro só se atualizava por ação da própria aba
    // (evento `refreshKanban`): lead novo entrando pelo robô, ou mudança feita por outro
    // usuário, só apareciam num F5.
    useNegociacoesRealtime({
        usuarioId: user?.id,
        pausado: loading || arrastando,
        aoAplicar: aplicarMudancasRealtime,
        aoRecarregarTudo: recarregarBoard,
        deveIgnorar: eventoDeOutroFunil,
    });

    const fetchProfiles = async () => {
        // Conta de consumidor (Portal) nunca entra em lista de escolha do sistema.
        const { data } = await supabase.from('profiles').select('*').neq('role', 'consumidor');
        if (data) setProfiles(data);
    };

    const fetchPipelines = async () => {
        const { data, error } = await supabase
            .from('pipelines')
            .select('*')
            .order('created_at', { ascending: true })
            .order('id', { ascending: true });
        if (error) {
            console.error('Error fetching pipelines:', error);
            setLoading(false);
            return;
        }

        if (data && data.length > 0) {
            setPipelines(data);

            // Restaura a escolha do próprio usuário; no primeiro acesso abre no funil
            // de venda nova. Antes era data[0] de um order('created_at') empatado, ou
            // seja, sorteio — e o resultado ficava gravado numa chave global.
            const savedPipelineId = lerFunilAtivo(user?.id);
            const isValidSaved = savedPipelineId && data.some(p => p.id === savedPipelineId);

            if (isValidSaved) {
                setActivePipelineId(savedPipelineId);
            } else if (!activePipelineId) {
                setActivePipelineId(funilDeVendas(data)?.id ?? data[0].id);
            }
        } else {
            setLoading(false); // Fix: Ensure loading stops if no pipelines found
        }
    };

    const fetchStages = async (pipelineId: string) => {
        const { data, error } = await supabase
            .from('pipeline_stages')
            .select('*')
            .eq('pipeline_id', pipelineId)
            .order('position', { ascending: true });

        if (error) {
            console.error('Error fetching stages:', error);
        } else if (data) {
            setStages(data);
        }
    };

    // Auto-open modal if negociacaoId is in URL
    useEffect(() => {
        const negociacaoId = searchParams.get('negociacaoId');
        if (!negociacaoId) return;

        const openByParam = async () => {
            // First check if already in local state (check by string and number)
            const localMatch = negociacoes.find(n => String(n.id) === String(negociacaoId));

            if (localMatch) {
                setSelectedNegociacao(localMatch);
                setShowDetailsModal(true);
                // Clear param
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('negociacaoId');
                setSearchParams(newParams);
            } else if (!loading) {
                // If not loading and not found locally, fetch it directly
                try {
                    const { data, error } = await supabase
                        .from('negociacoes')
                        .select('*, tarefas(id, status, vencimento), unidades_consumidoras(numero_uc, cidade)')
                        .eq('id', negociacaoId)
                        .single();

                    if (error) throw error;
                    if (data) {
                        setSelectedNegociacao(data as Negociacao);
                        setShowDetailsModal(true);
                        // Clear param
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('negociacaoId');
                        setSearchParams(newParams);
                    } else {
                        // Not found or no access, clear param to avoid infinite loop
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('negociacaoId');
                        setSearchParams(newParams);
                    }
                } catch (err) {
                    console.error('Error fetching negotiation from URL param:', err);
                    // Clear param even on error
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('negociacaoId');
                    setSearchParams(newParams);
                }
            }
        };

        openByParam();
    }, [searchParams, negociacoes, loading, setSearchParams]);

    const handleDragEnd = async (result: DropResult) => {
        setArrastando(false); // destrava o tempo real (ver useNegociacoesRealtime)
        const { destination, source, draggableId } = result;

        if (!destination) return;

        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        const newStage = destination.droppableId; // Now a string (UUID or name)

        // Optimistic Update
        const previousState = [...negociacoes];
        const updatedState = negociacoes.map(n =>
            String(n.id) === draggableId ? { ...n, stage: newStage } : n
        );

        setNegociacoes(updatedState);

        try {
            const { error } = await supabase
                .from('negociacoes')
                .update({ stage: newStage, updated_at: new Date().toISOString() })
                .eq('id', draggableId);

            if (error) throw error;

            // Log action
            await logAction(user?.id, 'UPDATE_NEGOCIACAO_STAGE', {
                negociacao_id: draggableId,
                old_stage: source.droppableId,
                new_stage: newStage
            });
            // Evento estruturado de etapa (alimenta o Funil Dinâmico)
            const movedStatus = previousState.find(n => String(n.id) === draggableId)?.status ?? null;
            logStageEvent({
                negociacaoId: draggableId,
                userId: user?.id,
                fromStage: source.droppableId,
                toStage: newStage,
                fromStatus: movedStatus,
                toStatus: movedStatus,
            });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'mover_etapa', origem: 'acao' });
        } catch (error) {
            console.error('Error updating stage:', error);
            setNegociacoes(previousState); // Revert
            alert('Erro ao mover negociação.');
        }
    };

    const handleOpenDetails = useCallback((negociacao: Negociacao) => {
        setSelectedNegociacao(negociacao);
        setShowDetailsModal(true);
    }, []);

    const getFilteredNegociacoes = () => {
        let filtered = negociacoes.filter(n => {
            // Role specific base filtering
            if (profile?.role === 'gerente_parcerias') {
                const responsible = profiles.find(p => p.id === n.responsavel_id);
                if (responsible?.role !== 'parceiro' && responsible?.role !== 'vendedor_parceiro' && n.responsavel_id !== profile.id) return false;
            }

            // Hide parceiros if toggled off (only for gerente and adm)
            if (!showParceiros && (profile?.role === 'gerente' || profile?.role === 'adm')) {
                const responsible = profiles.find(p => p.id === n.responsavel_id);
                if (responsible?.role === 'parceiro' || responsible?.role === 'vendedor_parceiro') return false;
            }

            // Hide specific statuses if toggled off
            if (!mostrarReprovados && n.status === 'termo_reprovado') return false;
            if (!mostrarPerdidas && n.status === 'perdido') return false;

            // 1. Status Filter (Multi-select)
            if (complexFilters.status?.length > 0) {
                if (!complexFilters.status.includes(n.status)) return false;
            }

            // Global search bar (nome, UC, cidade, CPF/CNPJ)
            if (globalSearch) {
                const q = globalSearch.toLowerCase().trim();
                const stripFmt = (s: string) => (s || '').replace(/[.\-\s\/]/g, '').toLowerCase();
                const qStripped = stripFmt(q);
                const matchesTitle = (n.titulo || '').toLowerCase().includes(q);
                const matchesCpf = stripFmt(n.cpf_cnpj || '').includes(qStripped);
                const ucsArr: any[] = Array.isArray((n as any).unidades_consumidoras)
                    ? (n as any).unidades_consumidoras
                    : ((n as any).unidades_consumidoras ? [(n as any).unidades_consumidoras] : []);
                const matchesUC = ucsArr.some((u: any) =>
                    stripFmt(u.numero_uc || '').includes(qStripped) ||
                    (u.cidade || '').toLowerCase().includes(q)
                );
                if (!matchesTitle && !matchesCpf && !matchesUC) return false;
            }

            // 2. Name Filter
            if (complexFilters.nome && !n.titulo?.toLowerCase().includes(complexFilters.nome.toLowerCase())) return false;

            // 3. Qualification Filter (Stars)
            if (complexFilters.qualificacao !== null && (n as any).avaliacao_estrelas !== complexFilters.qualificacao) return false;

            // 4. Consumption Filter
            const consumo = Number(n.consumo_medio_kwh || 0);
            if (complexFilters.consumoMin && consumo < Number(complexFilters.consumoMin)) return false;
            if (complexFilters.consumoMax && consumo > Number(complexFilters.consumoMax)) return false;

            // 5. Date Creation (Range)
            if (complexFilters.dataCriacaoInicio || complexFilters.dataCriacaoFim) {
                const nDate = new Date(n.created_at).getTime();

                // "T00:00:00"/"T23:59:59.999" sem timezone são interpretados no fuso local do
                // navegador — evita o bug de "new Date('2026-07-08')" (meia-noite UTC) + setHours
                // (que muta em horário local), que descartava o dia inteiro em fusos negativos.
                if (complexFilters.dataCriacaoInicio) {
                    const startDate = new Date(complexFilters.dataCriacaoInicio + 'T00:00:00').getTime();
                    if (nDate < startDate) return false;
                }

                if (complexFilters.dataCriacaoFim) {
                    const endDate = new Date(complexFilters.dataCriacaoFim + 'T23:59:59.999').getTime();
                    if (nDate > endDate) return false;
                }
            }

            // 6. Date Interaction (updated_at)
            if (complexFilters.dataInteracao) {
                const nDate = new Date(n.updated_at).toISOString().split('T')[0];
                if (nDate !== complexFilters.dataInteracao) return false;
            }

            // 7. Next Task
            if (complexFilters.dataTarefa) {
                const hasTask = (n as any).tarefas?.some((t: any) => {
                    return t.vencimento && t.vencimento.startsWith(complexFilters.dataTarefa);
                });
                if (!hasTask) return false;
            }

            // 8. Origin (Multi-select)
            if (complexFilters.origem?.length > 0) {
                const itemOrigem = n.origem || 'Desconhecido';
                if (!complexFilters.origem.includes(itemOrigem)) return false;
            }

            // 9. Plataforma / rede social (Multi-select)
            if (complexFilters.plataforma?.length > 0) {
                const itemPlataforma = normalizePlataforma((n as any).plataforma);
                if (!complexFilters.plataforma.includes(itemPlataforma)) return false;
            }

            // Responsible Filter
            if (selectedResponsibles.length > 0) {
                if (!n.responsavel_id || !selectedResponsibles.includes(n.responsavel_id)) return false;
            }

            return true;
        });

        // Sort Logic
        filtered.sort((a, b) => {
            switch (sortOption) {
                case 'created_asc':
                    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                case 'created_desc':
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                case 'consumo_desc':
                    return (b.consumo_medio_kwh || 0) - (a.consumo_medio_kwh || 0);
                case 'consumo_asc':
                    return (a.consumo_medio_kwh || 0) - (b.consumo_medio_kwh || 0);
                case 'updated_asc': // Esfriando (Oldest interaction first)
                    return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
                case 'rating_desc': // Qualificação (Higher rating first)
                    return (b.avaliacao_estrelas || 0) - (a.avaliacao_estrelas || 0);
                default:
                    return 0;
            }
        });

        return filtered;
    };

    // Base = todos os filtros existentes; a barra "Meu Trabalho Hoje" calcula sobre essa base.
    // useMemo: getFilteredNegociacoes é O(n log n) (filter + sort) — sem isso, qualquer
    // setState do board (abrir um dropdown, digitar na busca) recalculava tudo do zero.
    const baseFiltered = useMemo(() => getFilteredNegociacoes(), [
        negociacoes, profile?.role, profile?.id, profiles, showParceiros, mostrarReprovados,
        mostrarPerdidas, complexFilters, globalSearch, sortOption, selectedResponsibles,
    ]);
    const workCounts = useMemo(() => computeWorkCounts(baseFiltered), [baseFiltered]);
    const favoritosCount = useMemo(() => baseFiltered.filter(n => n.favorito).length, [baseFiltered]);

    // Aplica o contador (toggle) + o chip (seleção única) e sobe quem exige ação ao topo.
    const filtered = useMemo(() => [...baseFiltered]
        .filter(n => (workCounter ? matchCounter(n, workCounter) : true))
        .filter(n => matchChip(n, workChip))
        .sort((a, b) => Number(precisaAcao(b)) - Number(precisaAcao(a))), // estável: preserva o sort anterior dentro de cada grupo
        [baseFiltered, workCounter, workChip]);

    // Resultado do mês da barra "Meu Trabalho" — segue só o filtro de QUEM (responsáveis
    // + cortes por papel abaixo, iguais aos de getFilteredNegociacoes). Funil, status,
    // datas e busca não entram: o resultado do vendedor no mês é um só.
    const perfilPorId = useMemo(() => new Map(profiles.map(p => [p.id, p])), [profiles]);
    const responsavelVisivel = useCallback((id: string) => {
        const resp = perfilPorId.get(id);
        const ehDeParceiro = resp?.role === 'parceiro' || resp?.role === 'vendedor_parceiro';
        if (profile?.role === 'gerente_parcerias' && !ehDeParceiro && id !== profile.id) return false;
        if (!showParceiros && (profile?.role === 'gerente' || profile?.role === 'adm') && ehDeParceiro) return false;
        return true;
    }, [perfilPorId, profile?.role, profile?.id, showParceiros]);
    const resultadoMes = useResultadoMes({
        selecionados: selectedResponsibles,
        escopoResponsaveis,
        perfis: profiles,
        responsavelVisivel,
        ativo: !!profile?.id,
    });

    // Toggle de escopo da barra (gestor): "Eu" = só meus leads · "Todos" = todos
    const canToggleScope = profile?.role === 'gerente' || profile?.role === 'adm';
    const workScope: 'eu' | 'todos' = (selectedResponsibles.length === 1 && selectedResponsibles[0] === profile?.id) ? 'eu' : 'todos';
    const changeWorkScope = (s: 'eu' | 'todos') => setSelectedResponsibles(s === 'eu' && profile?.id ? [profile.id] : []);

    const stageNames = useMemo(() => new Set(stages.map(s => s.name)), [stages]);

    // Agrupa `filtered` por etapa numa única passada (em vez de 1 filter() por coluna).
    // Negociações sem etapa reconhecida (n.stage vazio ou "órfão") caem no bucket da
    // primeira etapa — mesma regra do antigo parâmetro `isFirstStage`. A ordem dentro de
    // cada bucket é a mesma de `filtered` (equivalente ao filter() original por coluna).
    const negociacoesPorStage = useMemo(() => {
        const map = new Map<string, Negociacao[]>();
        for (const s of stages) map.set(s.name, []);
        const firstStageName = stages[0]?.name;
        for (const n of filtered) {
            if (n.stage && stageNames.has(n.stage)) {
                map.get(n.stage)!.push(n);
            } else if (firstStageName) {
                map.get(firstStageName)?.push(n);
            }
        }
        return map;
    }, [filtered, stages, stageNames]);

    const getNegociacoesForStage = useCallback(
        (stageName: string) => negociacoesPorStage.get(stageName) || [],
        [negociacoesPorStage],
    );

    // Count active filters
    let activeFiltersCount = 0;
    if (complexFilters.status?.length > 0) activeFiltersCount++;
    if (complexFilters.nome) activeFiltersCount++;
    if (complexFilters.qualificacao !== null) activeFiltersCount++;
    if (complexFilters.consumoMin || complexFilters.consumoMax) activeFiltersCount++;
    if (complexFilters.dataCriacaoInicio || complexFilters.dataCriacaoFim) activeFiltersCount++;
    if (complexFilters.dataInteracao) activeFiltersCount++;
    if (complexFilters.dataTarefa) activeFiltersCount++;
    if (complexFilters.origem?.length > 0) activeFiltersCount++;
    if (complexFilters.plataforma?.length > 0) activeFiltersCount++;
    if (selectedResponsibles.length > 0) activeFiltersCount++;

    const termosReprovados = useMemo(
        () => negociacoes.filter(n => n.status === 'termo_reprovado' && n.responsavel_id === profile?.id),
        [negociacoes, profile?.id],
    );

    if (loading && pipelines.length === 0) return (
        <LoadingSpinner label="Carregando CRM..." size="lg" fullPage />
    );

    return (
        <div className="animate-fade-in flex flex-col h-[calc(100vh-180px)]">
            {/* Complete seus dados bancários — só parceiro/vendedor_parceiro, no topo da tela */}
            {isParceiroRole && temContaBancaria === false && !bancoBannerDispensado && (
                <div className="relative flex items-center justify-between gap-3 flex-wrap bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl px-4 py-3 mb-3 pr-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 shrink-0">
                            <Landmark size={18} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-amber-800 dark:text-amber-300">Complete seus dados bancários</p>
                            <p className="text-xs text-amber-700/80 dark:text-amber-400/80">Para receber seus pagamentos e bônus, cadastre sua conta bancária.</p>
                        </div>
                    </div>
                    <Link
                        to="/config/dados-bancarios"
                        className="shrink-0 px-4 py-2 rounded-full text-sm font-bold bg-amber-500 text-white hover:bg-amber-600 transition-colors whitespace-nowrap"
                    >
                        Preencher agora
                    </Link>
                    <button
                        onClick={() => setBancoBannerDispensado(true)}
                        title="Dispensar"
                        className="absolute top-3 right-3 p-1 rounded-full text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Header / Filter Bar
                No celular são só três faixas antes dos cards: esta barra (uma linha de botões
                compactos), a busca e o Meu Trabalho recolhido. Funil, responsável, personalizar
                e ordenar ficam atrás da seta da direita. */}
            <div className="bg-white dark:bg-gray-800 p-2 md:p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-2 md:mb-6 transition-colors">

                {/* Single row on desktop, stacked on mobile.
                    md:flex-wrap: entre 768px e ~1150px os dois grupos não cabem lado a lado
                    (o caso do Bruno: iPhone em "solicitar site para computador" = 980px).
                    Sem isso a barra estourava a viewport e os controles da direita ficavam
                    fora da tela, com a página inteira rolando na horizontal. */}
                <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-3 md:gap-2">

                    {/* Left: Dashboard + Filters + View Toggle
                        No celular os botões ficam só no ícone (p-2, sem a setinha dos menus) e com
                        gap-1 para caber numa linha até no papel com mais botões (adm) em 360px. */}
                    <div className="flex items-center flex-wrap gap-1 md:gap-2 md:flex-shrink-0 min-w-0">

                        {/* Relatórios Dropdown (Dashboard + KPI) — não se aplica ao parceiro */}
                        {!isParceiroRole && (
                            <div className="relative group">
                                <button
                                    onClick={() => { setShowRelatoriosDropdown(!showRelatoriosDropdown); setShowComercialDropdown(false); }}
                                    className={`flex items-center gap-1 p-2 md:p-2.5 text-sm font-semibold border rounded-full transition-all shadow-sm bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600
                                        ${showRelatoriosDropdown ? 'ring-2 ring-primary/10 border-primary' : ''}
                                    `}
                                >
                                    <BarChart3 size={16} />
                                    <ChevronDown size={13} className={`hidden md:block text-gray-400 transition-transform duration-300 ${showRelatoriosDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                <BarraTooltip label="Relatórios" hidden={showRelatoriosDropdown} />

                                {showRelatoriosDropdown && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setShowRelatoriosDropdown(false)}></div>
                                        <div className="absolute top-full left-0 mt-2 w-auto min-w-[180px] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-1.5 z-50 animate-fade-in">
                                            <button
                                                onClick={() => { setShowDashboard(true); setShowRelatoriosDropdown(false); }}
                                                className="w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                                            >
                                                <BarChart3 size={15} className="text-gray-400" />
                                                Dashboard
                                            </button>

                                            {(profile?.role === 'gerente' || profile?.role === 'adm') && (
                                                <button
                                                    onClick={() => { setShowPerformanceDashboard(true); setShowRelatoriosDropdown(false); }}
                                                    className="w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                                                >
                                                    <Zap size={15} className="text-amber-500" />
                                                    Performance Comercial
                                                </button>
                                            )}

                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Comercial Dropdown (Indicações + Parceiros) */}
                        {(profile?.role === 'adm' || profile?.role === 'gerente' || ehComercialInterno(profile)) && (
                            <div className="relative group">
                                <button
                                    onClick={() => { setShowComercialDropdown(!showComercialDropdown); setShowRelatoriosDropdown(false); }}
                                    className={`flex items-center gap-1 p-2 md:p-2.5 text-sm font-semibold border rounded-full transition-all shadow-sm bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600
                                        ${showComercialDropdown ? 'ring-2 ring-primary/10 border-primary' : ''}
                                    `}
                                >
                                    <Handshake size={16} />
                                    <ChevronDown size={13} className={`hidden md:block text-gray-400 transition-transform duration-300 ${showComercialDropdown ? 'rotate-180' : ''}`} />
                                </button>
                                <BarraTooltip label="Comercial" hidden={showComercialDropdown} />

                                {showComercialDropdown && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setShowComercialDropdown(false)}></div>
                                        <div className="absolute top-full left-0 mt-2 w-auto min-w-[180px] bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-1.5 z-50 animate-fade-in">
                                            {/* Indicações — Closers e BDRs + gerente/adm */}
                                            {(profile?.role === 'adm' || profile?.role === 'gerente' || ehComercialInterno(profile)) && (
                                                <button
                                                    onClick={() => { setShowComercialDropdown(false); navigate('/crm/indicacoes'); }}
                                                    className="w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                                                >
                                                    <Trophy size={15} className="text-gray-400" />
                                                    Indicações
                                                </button>
                                            )}

                                            {/* Parceiros (interessados em parceria) — só adm e gerente, os mesmos que a RLS de `parceiros` libera (#6510) */}
                                            {(profile?.role === 'adm' || profile?.role === 'gerente') && (
                                                <button
                                                    onClick={() => { setShowParceirosModal(true); setShowComercialDropdown(false); }}
                                                    className="w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                                                >
                                                    <Handshake size={15} className="text-gray-400" />
                                                    Parceiros
                                                </button>
                                            )}

                                            {/* Avisar ausência — Closers e BDRs, que têm escala (a própria escala é read-only pra ela; isso só cria um pedido pro gestor aprovar) */}
                                            {ehComercialInterno(profile) && (
                                                <button
                                                    onClick={() => { setShowAvisarAusencia(true); setShowComercialDropdown(false); }}
                                                    className="w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                                                >
                                                    <CalendarOff size={15} className="text-gray-400" />
                                                    Avisar ausência
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Central de Custos da API Lemit — somente gerentes/admins */}
                        {(profile?.role === 'adm' || profile?.role === 'gerente') && (
                            <div className="relative group">
                                <button
                                    onClick={() => setShowLemitCentral(true)}
                                    className="flex items-center gap-2 p-2 md:p-2.5 text-sm font-semibold border rounded-full transition-all shadow-sm bg-white text-gray-700 border-gray-200 hover:bg-[#5F6C37]/5 hover:text-[#5F6C37] hover:border-[#5F6C37]/30 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-[#5F6C37]/20"
                                >
                                    <Wallet size={16} />
                                </button>
                                <BarraTooltip label="Central de Custos da API Lemit — saldo, consumo e registro de consultas" />
                            </div>
                        )}

                        {/* Filter Button (Opens Modal) — não se aplica ao Hunter */}
                        {!isVendedorExterno && (
                            <div className="relative group">
                                <button
                                    onClick={() => setShowFilterModal(true)}
                                    className={`relative flex items-center gap-2 p-2 md:p-2.5 text-sm font-semibold border rounded-full transition-all shadow-sm
                                        ${activeFiltersCount > 0
                                            ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/30'
                                            : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                                        }
                                    `}
                                >
                                    <SlidersHorizontal size={16} />
                                    {activeFiltersCount > 0 && (
                                        // No celular o número vira bolinha no canto do botão, para não alargá-lo
                                        <span className="absolute -top-1.5 -right-1.5 md:static bg-primary text-white text-[10px] w-4 h-4 md:w-5 md:h-5 flex items-center justify-center rounded-full">
                                            {activeFiltersCount}
                                        </span>
                                    )}
                                </button>
                                <BarraTooltip label={activeFiltersCount > 0 ? `Filtros (${activeFiltersCount} ativos)` : 'Filtros'} />
                            </div>
                        )}

                        {/* Export to Excel Button — não se aplica ao parceiro nem ao Hunter */}
                        {!isParceiroRole && !isVendedorExterno && (
                            <div className="relative group">
                                <button
                                    onClick={() => setShowExportModal(true)}
                                    className="flex items-center gap-2 p-2 md:p-2.5 text-sm font-semibold border rounded-full transition-all shadow-sm bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-400 dark:hover:border-emerald-800"
                                >
                                    <FileDown size={16} />
                                </button>
                                <BarraTooltip label="Exportar negociações filtradas para Excel" />
                            </div>
                        )}

                        {/* Termos Reprovados — visível a todos os papéis, só aparece quando há negociações reprovadas */}
                        {termosReprovados.length > 0 && (
                            <button
                                onClick={() => setShowTermosReprovadosModal(true)}
                                className="relative flex items-center gap-2 p-2 md:px-4 md:py-2 text-sm font-semibold border rounded-full transition-all shadow-sm bg-red-50 text-red-700 border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/40 dark:hover:bg-red-900/30"
                                title="Negociações com termo reprovado na validação de dados"
                            >
                                <XOctagon size={16} />
                                <span className="hidden md:inline">Termos Reprovados</span>
                                <span className="absolute -top-1.5 -right-1.5 md:static bg-red-600 text-white text-[10px] w-4 h-4 md:w-5 md:h-5 flex items-center justify-center rounded-full">
                                    {termosReprovados.length}
                                </span>
                            </button>
                        )}

                        {/* View Toggle — não se aplica ao Hunter (só existe a visão Kanban pra ele, não há o que alternar) */}
                        {!isVendedorExterno && (
                            <div className="flex items-center p-0.5 md:p-1 bg-gray-100 dark:bg-gray-700/50 rounded-full border border-gray-200 dark:border-gray-700">
                                <div className="relative group">
                                    <button
                                        onClick={() => setViewMode('kanban')}
                                        className={`p-1.5 rounded-full transition-all ${viewMode === 'kanban'
                                            ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                            }`}
                                    >
                                        <KanbanIcon size={18} />
                                    </button>
                                    <BarraTooltip label="Visualização Kanban" />
                                </div>
                                <div className="relative group">
                                    <button
                                        onClick={() => setViewMode('table')}
                                        className={`p-1.5 rounded-full transition-all ${viewMode === 'table'
                                            ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                            }`}
                                    >
                                        <Table size={18} />
                                    </button>
                                    <BarraTooltip label="Visualização em Tabela" />
                                </div>
                                {!isParceiroRole && (
                                    <div className="relative group">
                                        <button
                                            onClick={() => navigate('/crm/mapa')}
                                            className="p-1.5 rounded-full transition-all text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        >
                                            <MapIcon size={18} />
                                        </button>
                                        <BarraTooltip label="Mapa Inteligente" />
                                    </div>
                                )}
                            </div>
                        )}



                        {/* Mobile Toggle Button */}
                        <button
                            onClick={() => setShowMobileFilters(!showMobileFilters)}
                            title="Funil, responsável, personalizar e ordenar"
                            className={`flex md:hidden items-center justify-center p-2 rounded-full transition-all ml-auto border
                                ${showMobileFilters ? 'bg-primary/10 text-primary border-primary/20' : 'bg-white text-gray-500 border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400'}
                            `}
                        >
                            <ChevronDown size={16} className={`transition-transform duration-300 ${showMobileFilters ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Divider - desktop only */}
                        <div className="hidden md:block h-6 w-px bg-gray-200 dark:bg-gray-700"></div>
                    </div>

                    {/* Center: Pipeline + Responsible dropdowns - full width on mobile.
                        md:flex-wrap: em larguras "de tablet" (ex: iPad portrait, 768px) os botões
                        não cabem todos numa linha só — sem isso o texto quebrava dentro de cada
                        pill (ficava feio/quebrado) em vez de o botão inteiro ir pra linha de baixo. */}
                    <div className={`${showMobileFilters ? 'flex' : 'hidden md:flex'} flex-col md:flex-row md:flex-wrap gap-2 md:gap-2 w-full md:w-auto md:flex-auto md:items-center mt-2 md:mt-0 min-w-0`}>

                        {/* Pipeline Selector */}
                        <div className="relative w-full md:w-auto">
                            <button
                                onClick={() => setShowPipelineDropdown(!showPipelineDropdown)}
                                className={`flex items-center gap-2 px-3 md:px-3 py-2.5 md:py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:border-gray-300 dark:hover:border-gray-500 transition-all w-full md:w-auto justify-between group
                                    ${showPipelineDropdown ? 'ring-2 ring-primary/10 border-primary' : ''}
                                `}
                            >
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                                    <Filter size={15} className="text-gray-400 dark:text-gray-500 group-hover:text-primary transition-colors" />
                                    <span className="text-sm font-medium truncate">
                                        {pipelines.find(p => p.id === activePipelineId)?.name || 'Funil'}
                                    </span>
                                </div>
                                <ChevronDown size={13} className={`text-gray-400 transition-transform duration-300 ${showPipelineDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown - bottom sheet on mobile, absolute on desktop */}
                            {showPipelineDropdown && (
                                <>
                                    <div className="fixed inset-0 z-40 bg-black/30 md:bg-transparent" onClick={() => setShowPipelineDropdown(false)}></div>
                                    <div className="fixed bottom-0 left-0 right-0 md:absolute md:bottom-auto md:top-full md:left-0 md:right-auto mt-0 md:mt-2 w-full md:w-auto md:min-w-[220px] bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-3 md:p-2 z-50 animate-fade-in">
                                        <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-3 md:hidden"></div>
                                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 mb-2 md:hidden">Selecione o funil</p>
                                        {pipelines.map(pipeline => (
                                            <button
                                                key={pipeline.id}
                                                onClick={() => {
                                                    setActivePipelineId(pipeline.id);
                                                    setShowPipelineDropdown(false);
                                                }}
                                                className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center justify-between mb-1 last:mb-0
                                                    ${activePipelineId === pipeline.id
                                                        ? 'bg-primary/5 dark:bg-primary/10 text-primary font-bold'
                                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                                                    }
                                                `}
                                            >
                                                {pipeline.name}
                                                {activePipelineId === pipeline.id && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Responsible Selector — não se aplica ao parceiro (só vê os próprios leads) */}
                        {!isParceiroRole && (
                            <ResponsibleFilter
                                selectedResponsibles={selectedResponsibles}
                                setSelectedResponsibles={setSelectedResponsibles}
                                profiles={profiles}
                                currentUserProfile={profile}
                                viewContext="crm"
                            />
                        )}

                        {/* Divider - desktop only */}
                        <div className="hidden md:block h-6 w-px bg-gray-200 dark:bg-gray-700"></div>

                        {/* Personalizar visualização Dropdown */}
                        <div className="relative w-full md:w-auto">
                            <button
                                onClick={() => setShowCustomizeDropdown(!showCustomizeDropdown)}
                                className={`flex items-center gap-2 px-3 py-2.5 md:py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:border-gray-300 dark:hover:border-gray-500 transition-all w-full md:w-auto justify-between group
                                    ${showCustomizeDropdown ? 'ring-2 ring-primary/10 border-primary' : ''}
                                `}
                            >
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                                    <Settings2 size={15} className="text-gray-400 dark:text-gray-500 group-hover:text-primary transition-colors" />
                                    <span className="text-sm font-medium whitespace-nowrap">Personalizar visualização</span>
                                </div>
                                <ChevronDown size={13} className={`text-gray-400 transition-transform duration-300 ${showCustomizeDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showCustomizeDropdown && (
                                <>
                                    <div className="fixed inset-0 z-40 bg-black/30 md:bg-transparent" onClick={() => setShowCustomizeDropdown(false)}></div>
                                    <div className="fixed bottom-0 left-0 right-0 md:absolute md:bottom-auto md:top-full md:left-0 md:right-auto mt-0 md:mt-2 w-full md:w-auto md:min-w-[250px] bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-3 md:p-2 z-50 animate-fade-in">
                                        <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-3 md:hidden"></div>
                                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 mb-2 md:hidden">Personalizar visualização</p>

                                        {/* Parceiros - only for gerente/adm */}
                                        {(profile?.role === 'gerente' || profile?.role === 'adm') && (
                                            <button
                                                onClick={() => setShowParceiros(!showParceiros)}
                                                className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center gap-3 mb-1
                                                    ${showParceiros
                                                        ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                                                        : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                    }
                                                `}
                                            >
                                                {showParceiros ? <Eye size={15} /> : <EyeOff size={15} />}
                                                Parceiros
                                            </button>
                                        )}

                                        {/* Termo Reprovado */}
                                        <button
                                            onClick={() => setShowTermoReprovado(!showTermoReprovado)}
                                            aria-pressed={mostrarReprovados}
                                            title={forcadoPeloFiltro('termo_reprovado') ? 'Aparecem porque o filtro de status pede "Termo reprovado"' : undefined}
                                            className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center gap-3 mb-1
                                                ${mostrarReprovados
                                                    ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                                                    : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                }
                                            `}
                                        >
                                            {mostrarReprovados ? <Eye size={15} /> : <EyeOff size={15} />}
                                            Termo Reprovado
                                        </button>

                                        {/* Perdidas */}
                                        <button
                                            onClick={() => setShowPerdidas(!showPerdidas)}
                                            aria-pressed={mostrarPerdidas}
                                            title={forcadoPeloFiltro('perdido') ? 'Aparecem porque o filtro de status pede "Perdido"' : undefined}
                                            className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center gap-3 mb-1
                                                ${mostrarPerdidas
                                                    ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                                                    : 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                }
                                            `}
                                        >
                                            {mostrarPerdidas ? <Eye size={15} /> : <EyeOff size={15} />}
                                            Perdidas
                                        </button>

                                        {/* Divider */}
                                        <div className="h-px bg-gray-100 dark:bg-gray-700 my-1 mx-2"></div>

                                        {/* Visualização Enxuta */}
                                        <button
                                            onClick={() => setIsLeanView(!isLeanView)}
                                            className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center gap-3
                                                ${isLeanView
                                                    ? 'text-primary font-bold bg-primary/5 dark:bg-primary/10'
                                                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                                                }
                                            `}
                                        >
                                            {isLeanView ? <Eye size={15} /> : <EyeOff size={15} />}
                                            Visualização Enxuta
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Compact Search - desktop only, não se aplica ao Hunter (usa a barra larga abaixo) */}
                        {!isVendedorExterno && (
                        <div className="hidden md:flex items-center relative">
                            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none z-10" />
                            <input
                                type="text"
                                value={globalSearch}
                                onChange={e => setGlobalSearch(e.target.value)}
                                placeholder="Buscar..."
                                className={`pl-7 py-1.5 text-sm bg-gray-50 dark:bg-gray-700/60 border rounded-full text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200
                                    ${globalSearch
                                        ? 'w-52 pr-6 border-primary/40 dark:border-primary/40 bg-white dark:bg-gray-700'
                                        : 'w-24 pr-2.5 border-gray-200 dark:border-gray-600 focus:w-52 focus:pr-6 focus:border-primary/40 focus:bg-white dark:focus:bg-gray-700'
                                    }`}
                            />
                            {globalSearch && (
                                <button
                                    onClick={() => setGlobalSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                >
                                    <X size={11} />
                                </button>
                            )}
                        </div>
                        )}

                        {/* Sort Selector - pushed right on desktop */}
                        <div className="relative w-full md:w-auto md:ml-auto">
                            <button
                                onClick={() => setShowSortDropdown(!showSortDropdown)}
                                className={`flex items-center gap-2 px-3 py-2.5 md:py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:border-gray-300 dark:hover:border-gray-500 transition-all w-full md:w-auto justify-between group
                                    ${showSortDropdown ? 'ring-2 ring-primary/10 border-primary' : ''}
                                `}
                            >
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                                    <ArrowUpDown size={15} className="text-gray-400 dark:text-gray-500 group-hover:text-primary transition-colors" />
                                    <span className="text-sm font-medium whitespace-nowrap">
                                        {sortOption === 'created_desc' && 'Criadas por último'}
                                        {sortOption === 'created_asc' && 'Criadas primeiro'}
                                        {sortOption === 'consumo_desc' && 'Maior média (kWh)'}
                                        {sortOption === 'consumo_asc' && 'Menor média (kWh)'}
                                        {sortOption === 'updated_asc' && 'Esfriando 🧊'}
                                        {sortOption === 'rating_desc' && 'Qualificação ⭐'}
                                    </span>
                                </div>
                                <ChevronDown size={13} className={`text-gray-400 transition-transform duration-300 ${showSortDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown - bottom sheet on mobile */}
                            {showSortDropdown && (
                                <>
                                    <div className="fixed inset-0 z-40 bg-black/30 md:bg-transparent" onClick={() => setShowSortDropdown(false)}></div>
                                    <div className="fixed bottom-0 left-0 right-0 md:absolute md:bottom-auto md:top-full md:right-0 md:left-auto mt-0 md:mt-2 w-full md:w-auto md:min-w-[220px] bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-3 md:p-2 z-50 animate-fade-in">
                                        <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-3 md:hidden"></div>
                                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 mb-2 md:hidden">Ordenar por</p>
                                        {[
                                            { value: 'created_desc', label: 'Criadas por último ⏳' },
                                            { value: 'created_asc', label: 'Criadas primeiro ⏳' },
                                            { value: 'consumo_desc', label: 'Maior média (kWh) ⚡' },
                                            { value: 'consumo_asc', label: 'Menor média (kWh) ⚡' },
                                            { value: 'updated_asc', label: 'Esfriando 🥶' },
                                            { value: 'rating_desc', label: 'Maior Qualificação ⭐' }
                                        ].map(opt => (
                                            <button
                                                key={opt.value}
                                                onClick={() => {
                                                    setSortOption(opt.value);
                                                    setShowSortDropdown(false);
                                                }}
                                                className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center justify-between mb-1 last:mb-0
                                                    ${sortOption === opt.value
                                                        ? 'bg-primary/5 dark:bg-primary/10 text-primary font-bold'
                                                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                                                    }
                                                `}
                                            >
                                                {opt.label}
                                                {sortOption === opt.value && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>


                </div>
            </div>


            {/* Busca rápida — mobile em todos os papéis; para o Hunter também no desktop (substitui a busca compacta) */}
            <div className={`${isVendedorExterno ? '' : 'md:hidden'} px-1 mb-2 md:mb-3`}>
                <div className="relative">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
                    <input
                        type="text"
                        value={globalSearch}
                        onChange={e => setGlobalSearch(e.target.value)}
                        placeholder="Buscar negociação..."
                        className="w-full pl-9 pr-9 py-2 md:py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all"
                    />
                    {globalSearch && (
                        <button
                            onClick={() => setGlobalSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>

            {/* Busca com perdidas/reprovadas ocultas: sem este aviso, quem procura um
                cliente perdido conclui que ele não existe e cria a negociação de novo. */}
            {globalSearch.trim() && (!mostrarPerdidas || !mostrarReprovados) && (
                <div className="mx-1 mb-3 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                    <EyeOff size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                    <span>
                        {!mostrarPerdidas && !mostrarReprovados
                            ? 'Perdidas e termos reprovados estão ocultos e não aparecem na busca.'
                            : !mostrarPerdidas
                                ? 'Perdidas estão ocultas e não aparecem na busca.'
                                : 'Termos reprovados estão ocultos e não aparecem na busca.'}
                    </span>
                    <button
                        type="button"
                        onClick={() => { setShowPerdidas(true); setShowTermoReprovado(true); }}
                        className="font-semibold text-primary dark:text-primary-light hover:underline"
                    >
                        Mostrar
                    </button>
                </div>
            )}

            {/* Barra "Meu Trabalho" — resultado do mês + contadores clicáveis + meta + chips (doc 01) */}
            <MeuTrabalhoHoje
                resultado={resultadoMes}
                counts={workCounts}
                activeCounter={workCounter}
                onToggleCounter={(c) => setWorkCounter(prev => (prev === c ? null : c))}
                activeChip={workChip}
                onSelectChip={(c) => setWorkChip(prev => (prev === c ? 'todos' : c))}
                favoritosCount={favoritosCount}
                canToggleScope={canToggleScope}
                scope={workScope}
                onChangeScope={changeWorkScope}
                simpleChipFilter={isParceiroRole}
            />

            {/* Quadro vazio com negociações em outro funil: explica o vazio e leva até lá.
                Sem isto, a negociação recém-criada num funil diferente simplesmente não
                existe para o vendedor — foi o que aconteceu em ago/2026. */}
            {negociacoesEmOutrosFunis.length > 0 && (
                <div className="mx-1 mb-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 text-sm text-amber-900 dark:text-amber-200">
                        Nenhuma negociação neste funil.{' '}
                        {negociacoesEmOutrosFunis.map((f, i) => (
                            <React.Fragment key={f.id}>
                                {i > 0 && ' · '}
                                Você tem <strong>{f.total}</strong> em <strong>{f.name}</strong>.
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="flex gap-2 shrink-0">
                        {negociacoesEmOutrosFunis.map(f => (
                            <button
                                key={f.id}
                                onClick={() => setActivePipelineId(f.id)}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                            >
                                Ver {f.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Main Content Area */}
            <div className="flex-1 overflow-hidden flex flex-col min-h-0 pb-2 px-1">
                {viewMode === 'kanban' ? (
                    <div className="overflow-y-auto overflow-x-hidden md:overflow-y-hidden md:overflow-x-auto custom-scrollbar h-full">
                        <DragDropContext onDragStart={() => setArrastando(true)} onDragEnd={handleDragEnd}>
                            {stages.length > 0 ? (
                                <div className="flex flex-col md:flex-row gap-4 md:gap-6 md:h-full md:min-w-max px-1 md:px-2">
                                    {stages.map((stage) => (
                                        <KanbanColumn
                                            key={stage.name}
                                            id={stage.name}
                                            label={nomeEtapaNaTela(stage.name)}
                                            negociacoes={getNegociacoesForStage(stage.name)}
                                            onOpenDetails={handleOpenDetails}
                                            onCreateVisita={setVisitaNegociacao}
                                            color={stage.color || '#3b82f6'}
                                            profiles={profiles}
                                            isLeanView={isLeanView} // Pass isLeanView prop
                                            isAdmin={profile?.role === 'adm'}
                                            onChanged={recarregarBoard}
                                            naoAbertoIds={naoAbertoIds}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-center p-8 opacity-60">
                                    <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                                        <LayoutList size={32} className="text-gray-400 dark:text-gray-500" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200">Nenhuma etapa encontrada</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                                        Este funil não possui etapas configuradas ou não foi possível carregá-las.
                                    </p>
                                </div>
                            )}
                        </DragDropContext>
                    </div>
                ) : (
                    <CRMTable
                        negociacoes={filtered}
                        onOpenDetails={handleOpenDetails}
                        stages={stages}
                        profiles={profiles}
                        onUpdate={recarregarBoard}
                    />
                )}
            </div>

            {showTermosReprovadosModal && (
                <TermosReprovadosModal
                    negociacoes={termosReprovados}
                    profiles={profiles}
                    onClose={() => setShowTermosReprovadosModal(false)}
                    onOpenDetails={(n) => { setShowTermosReprovadosModal(false); handleOpenDetails(n); }}
                    onUpdate={recarregarBoard}
                />
            )}

            <NegotiationDetailModal
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    recarregarBoard(); // Refresh on close
                }}
                negotiationId={selectedNegociacao?.id || null}
                onUpdate={recarregarBoard}
            />

            {visitaNegociacao && (
                <CriarVisitaModal
                    negociacao={visitaNegociacao}
                    onClose={() => setVisitaNegociacao(null)}
                    onCreated={recarregarBoard}
                />
            )}

            <CRMFilterModal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                currentFilters={complexFilters}
                onApply={setComplexFilters}
            />

            <ExportExcelModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                negociacoes={filtered}
                profiles={profiles}
                pipelines={pipelines}
            />

            {(profile?.role === 'adm' || profile?.role === 'gerente') && (
                <LemitCentralCustosModal
                    isOpen={showLemitCentral}
                    onClose={() => setShowLemitCentral(false)}
                />
            )}

            <KanbanGraphic
                isOpen={showDashboard}
                onClose={() => setShowDashboard(false)}
                activePipelineId={activePipelineId}
                selectedResponsibles={selectedResponsibles}
                onOpenNegociacao={(id) => {
                    setShowDashboard(false);
                    const n = negociacoes.find(x => String(x.id) === String(id));
                    setSelectedNegociacao(n || ({ id } as Negociacao));
                    setShowDetailsModal(true);
                }}
            />

            <PerformanceDashboard
                isOpen={showPerformanceDashboard}
                onClose={() => setShowPerformanceDashboard(false)}
            />

            <ParceirosModal
                isOpen={showParceirosModal}
                onClose={() => setShowParceirosModal(false)}
            />

            <AvisarAusenciaModal
                isOpen={showAvisarAusencia}
                onClose={() => setShowAvisarAusencia(false)}
            />

        </div>
    );
};

export default KanbanBoard;
